import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { DevHubAlert } from '@devxdiscover/devhub-ui';
import { useGlobalPolicyAccess } from './useGlobalPolicyAccess';

export type PolicyAccessCheckProps = {
  /** The children to render if access is granted */
  children: ReactNode;
  /** The global policy name to check */
  policy: string;
  /** Optional fallback to render if access is denied. Defaults to redirect to home */
  fallback?: ReactNode;
  /** If true, renders nothing while loading. If false, renders children while loading */
  hideWhileLoading?: boolean;
};

/**
 * PolicyAccessCheck renders its children only if the current user has access
 * to the specified global policy via the fusion PolicyManager.
 * @param props - Component props
 * @returns Children if user has access, fallback otherwise
 */
export const PolicyAccessCheck = ({
  children,
  policy,
  fallback = <Navigate to="/" replace />,
  hideWhileLoading = true,
}: PolicyAccessCheckProps) => {
  const { hasAccess, loading, error } = useGlobalPolicyAccess(policy);

  if (error && !loading) {
    return (
      <DevHubAlert severity="error">
        Unable to verify policy access. Please try again later.
      </DevHubAlert>
    );
  }

  if (loading) {
    return hideWhileLoading ? null : <>{children}</>;
  }

  return <>{hasAccess ? children : fallback}</>;
};
